import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

export interface PortalSupplier {
  id: number;
  uuid: string;
  company_id: number;
  name: string;
  code: string;
  contact_person: string | null;
  email: string;
  phone: string | null;
  fax: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  country: string | null;
  postal_code: string | null;
  tax_number: string | null;
  vat_number: string | null;
  iban: string | null;
  bank_name: string | null;
  swift_code: string | null;
  currency_code: string | null;
  payment_terms: string | null;
  portal_enabled: boolean;
}

export interface PurchaseOrderItem {
  id: number;
  item_name: string | null;
  part_number: string | null;
  quantity: string;
  quantity_received: string;
  unit_price: string;
  line_total: string;
  total_price: string | null;
  status: string | null;
}

export interface PurchaseOrder {
  id: number;
  order_number: string | null;
  po_number: string | null;
  supplier_id: number;
  order_date: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  status: string;
  priority: string;
  subtotal: string;
  tax_amount: string;
  total: string;
  total_amount: string;
  currency_code: string;
  payment_terms: string | null;
  shipping_address: string | null;
  notes: string | null;
  items?: PurchaseOrderItem[];
}

export interface SupplierDashboard {
  summary: {
    total_orders: number;
    open_orders: number;
    partially_received: number;
    received: number;
    overdue: number;
    open_value: number;
    received_value: number;
  };
  recent_orders: PurchaseOrder[];
}

const supplierClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

supplierClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('supplierToken');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

supplierClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('supplierToken');
      window.dispatchEvent(new CustomEvent('supplier:unauthorized'));
    }
    return Promise.reject(error);
  }
);

export const supplierApi = {
  async register(email: string, password: string, password_confirmation: string) {
    const { data } = await supplierClient.post('/supplier/auth/register', { email, password, password_confirmation });
    return data.data as { supplier: PortalSupplier; token: string };
  },

  async login(email: string, password: string) {
    const { data } = await supplierClient.post('/supplier/auth/login', { email, password });
    return data.data as { supplier: PortalSupplier; token: string };
  },

  async logout() {
    await supplierClient.post('/supplier/auth/logout');
  },

  async me() {
    const { data } = await supplierClient.get('/supplier/auth/me');
    return data.data as { supplier: PortalSupplier };
  },

  async dashboard() {
    const { data } = await supplierClient.get('/supplier/dashboard');
    return data.data as SupplierDashboard;
  },

  async purchaseOrders(params: { status?: string; search?: string; page?: number; per_page?: number } = {}) {
    const { data } = await supplierClient.get('/supplier/purchase-orders', { params });
    return data.data as { current_page: number; last_page: number; data: PurchaseOrder[]; total: number };
  },

  async purchaseOrder(id: number) {
    const { data } = await supplierClient.get(`/supplier/purchase-orders/${id}`);
    return data.data as PurchaseOrder;
  },

  async confirmPurchaseOrder(id: number) {
    const { data } = await supplierClient.post(`/supplier/purchase-orders/${id}/confirm`);
    return data.data as PurchaseOrder;
  },

  async profile() {
    const { data } = await supplierClient.get('/supplier/profile');
    return data.data as PortalSupplier;
  },

  async updateProfile(payload: Partial<PortalSupplier>) {
    const { data } = await supplierClient.put('/supplier/profile', payload);
    return data.data as PortalSupplier;
  },
};

export default supplierClient;
